import { useEffect, useRef, useState, type ReactNode } from 'react'
import {
  LmsButton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shared/ui'
import type { FlipbookAnnotationColorTool, FlipbookAnnotationTool } from './FlipbookAnnotationLayer'
import type { FlipbookAnnotationHistoryControls, FlipbookTextAlignment } from './flipbook-annotation-contracts'
import { FLIPBOOK_MAX_ZOOM } from './flipbook-constants'
import { FlipbookIcon } from './flipbook-icons'
import { FlipbookPageJump } from './FlipbookPageJump'
import type { FlipbookAnnotationSyncStatus } from './useFlipbookAnnotationPersistence'
import './flipbook.css'

interface FlipbookToolbarProps {
  readonly bookTitle: string
  readonly displayPage: number
  readonly isFullscreen: boolean
  readonly isPageLoading: boolean
  readonly highlighterColor: string
  readonly annotationHistory: FlipbookAnnotationHistoryControls
  readonly onBack: () => void
  readonly onAnnotationColorChange: (tool: FlipbookAnnotationColorTool, color: string) => void
  readonly onAnnotationToolChange: (tool: FlipbookAnnotationTool) => void
  readonly onNextPage: () => void
  readonly onNavigateToPage: (page: number) => void
  readonly onPreviousPage: () => void
  readonly onToggleFullscreen: () => void
  readonly onZoomIn: () => void
  readonly onZoomOut: () => void
  readonly pageCount: number
  readonly pageNavigationKey: string
  readonly progress?: ReactNode
  readonly annotationTool: FlipbookAnnotationTool
  readonly annotationSyncStatus: FlipbookAnnotationSyncStatus
  readonly penColor: string
  readonly selectedTextColor: string | null
  readonly textColor: string
  readonly zoom: number
  readonly visiblePageNumbers: readonly number[]
}

const ANNOTATION_COLORS = [
  { label: 'Mực', value: 'var(--lms-flipbook-annotation-ink)' },
  { label: 'Đỏ', value: 'var(--lms-flipbook-annotation-red)' },
  { label: 'Cam', value: 'var(--lms-color-brand-accent)' },
  { label: 'Cam đậm', value: 'var(--lms-color-brand-action)' },
  { label: 'Vàng', value: 'var(--lms-color-brand-primary)' },
  { label: 'Xanh lá', value: 'var(--lms-flipbook-annotation-green)' },
  { label: 'Xanh ngọc', value: 'var(--lms-flipbook-annotation-teal)' },
  { label: 'Xanh dương', value: 'var(--lms-flipbook-annotation-blue)' },
  { label: 'Tím', value: 'var(--lms-flipbook-annotation-purple)' },
  { label: 'Hồng', value: 'var(--lms-flipbook-annotation-pink)' },
] as const

export function FlipbookToolbar({
  bookTitle,
  displayPage,
  isFullscreen,
  isPageLoading,
  highlighterColor,
  annotationHistory,
  onBack,
  onAnnotationColorChange,
  onAnnotationToolChange,
  onNextPage,
  onNavigateToPage,
  onPreviousPage,
  onToggleFullscreen,
  onZoomIn,
  onZoomOut,
  pageCount,
  pageNavigationKey,
  progress,
  annotationTool,
  annotationSyncStatus,
  penColor,
  selectedTextColor,
  textColor,
  zoom,
  visiblePageNumbers,
}: FlipbookToolbarProps) {
  const [openPaletteTool, setOpenPaletteTool] = useState<FlipbookAnnotationColorTool | null>(null)
  const [isAnnotationToolbarOpen, setIsAnnotationToolbarOpen] = useState(true)
  const colorPaletteRef = useRef<HTMLDivElement | null>(null)

  const getActiveColor = (tool: FlipbookAnnotationColorTool) => {
    if (tool === 'pen') {
      return annotationHistory.selectedStrokeTool === 'pen'
        ? annotationHistory.selectedStrokeColor ?? penColor
        : penColor
    }

    if (tool === 'highlighter') {
      return annotationHistory.selectedStrokeTool === 'highlighter'
        ? annotationHistory.selectedStrokeColor ?? highlighterColor
        : highlighterColor
    }

    return selectedTextColor ?? textColor
  }

  const handleColorToolClick = (tool: FlipbookAnnotationColorTool) => {
    if (annotationTool !== tool) {
      onAnnotationToolChange(tool)
    }
    setOpenPaletteTool((current) => current === tool ? null : tool)
  }

  const handleNonColorToolClick = (tool: FlipbookAnnotationTool) => {
    setOpenPaletteTool(null)
    onAnnotationToolChange(annotationTool === tool ? 'none' : tool)
  }

  const renderColorPalette = (tool: FlipbookAnnotationColorTool) => {
    if (openPaletteTool !== tool) {
      return null
    }

    const activeColor = getActiveColor(tool)
    return (
      <div aria-label={`${tool} colors`} className="flipbookAnnotationColorMenu" role="listbox">
        {ANNOTATION_COLORS.map((color) => (
          <button
            aria-label={color.label}
            aria-selected={activeColor === color.value}
            className={`flipbookAnnotationColorSwatch${activeColor === color.value ? ' isSelected' : ''}`}
            key={color.value}
            onClick={() => {
              onAnnotationColorChange(tool, color.value)
              setOpenPaletteTool(null)
            }}
            role="option"
            style={{ backgroundColor: color.value }}
            type="button"
          />
        ))}
      </div>
    )
  }

  const renderTextAlignment = (alignment: FlipbookTextAlignment, label: string, icon: ReactNode) => (
    <Tooltip key={alignment}>
      <TooltipTrigger asChild>
        <span>
          <LmsButton
            aria-label={label}
            aria-pressed={annotationHistory.selectedTextAlignment === alignment}
            className={`flipbookAnnotationTool${annotationHistory.selectedTextAlignment === alignment ? ' isActive' : ''}`}
            disabled={annotationHistory.selectedTextColor === null || isPageLoading}
            onClick={() => { annotationHistory.alignSelectedText(alignment) }}
            size="icon"
            variant="ghost"
          >
            {icon}
          </LmsButton>
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )

  const renderLayerAction = (label: string, icon: ReactNode, action: () => void) => (
    <Tooltip key={label}>
      <TooltipTrigger asChild>
        <span>
          <LmsButton
            aria-label={label}
            className="flipbookAnnotationTool"
            disabled={(annotationHistory.selectedTextColor === null && annotationHistory.selectedStrokeColor === null) || isPageLoading}
            onClick={action}
            size="icon"
            variant="ghost"
          >
            {icon}
          </LmsButton>
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )

  useEffect(() => {
    if (!openPaletteTool) {
      return
    }

    const handleOutsidePointerDown = (event: PointerEvent) => {
      if (!colorPaletteRef.current?.contains(event.target as Node)) {
        setOpenPaletteTool(null)
      }
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenPaletteTool(null)
      }
    }

    document.addEventListener('pointerdown', handleOutsidePointerDown)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('pointerdown', handleOutsidePointerDown)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [openPaletteTool])

  return (
    <TooltipProvider delayDuration={250}>
      <header className={`flipbookToolbarHeader${annotationHistory.selectedTextColor !== null || annotationHistory.selectedStrokeColor !== null ? ' hasSelection' : ''}`}>
        <div className="flipbookToolbarInner">
          <div className="flipbookToolbarTitleGroup">
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <LmsButton aria-label="Back" className="size-11 text-[var(--lms-color-ink-strong)] hover:bg-[var(--lms-color-surface-muted)]" onClick={onBack} size="icon" variant="ghost">
                    <FlipbookIcon className="size-5" name="arrow-left" />
                  </LmsButton>
                </span>
              </TooltipTrigger>
              <TooltipContent>Quay lại</TooltipContent>
            </Tooltip>
            <div className="flipbookToolbarTitleWrap">
              <p className="flipbookToolbarTitle">
                {bookTitle}
              </p>
              {annotationSyncStatus !== 'idle' ? (
                <span aria-live="polite" className={`flipbookToolbarSyncStatus is-${annotationSyncStatus}`}>
                  {annotationSyncStatus === 'loading' ? 'Đang tải ghi chú' : null}
                  {annotationSyncStatus === 'saving' ? 'Đang lưu' : null}
                  {annotationSyncStatus === 'saved' ? 'Đã lưu' : null}
                  {annotationSyncStatus === 'error' ? 'Chưa đồng bộ' : null}
                </span>
              ) : null}
            </div>
          <div className="flipbookToolbarPageControls">
            {pageCount > 0 ? (
              <FlipbookPageJump
                displayPage={displayPage}
                isPageLoading={isPageLoading}
                key={pageNavigationKey}
                onNavigateToPage={onNavigateToPage}
                pageCount={pageCount}
                visiblePageNumbers={visiblePageNumbers}
              />
            ) : null}
            {progress ? (
              <div className={`flipbookToolbarProgress${isFullscreen ? ' isFullscreen' : ''}`}>
                {progress}
              </div>
            ) : null}
          </div>
          </div>
          {pageCount > 0 ? (
            <div aria-label="Page navigation" className="flipbookToolbarNavigation">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <LmsButton aria-label="Previous page" className="size-11 bg-white text-[var(--lms-color-ink-strong)] shadow-md hover:bg-white" disabled={displayPage <= 1 || isPageLoading} onClick={onPreviousPage} size="icon" variant="outline">
                      <FlipbookIcon className="size-5" name="arrow-left" />
                    </LmsButton>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Trang trước</TooltipContent>
              </Tooltip>
            <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <LmsButton aria-label="Next page" className="size-11 bg-white text-[var(--lms-color-ink-strong)] shadow-md hover:bg-white" disabled={displayPage >= pageCount || isPageLoading} onClick={onNextPage} size="icon" variant="outline">
                      <FlipbookIcon className="size-5" name="arrow-right" />
                    </LmsButton>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Trang sau</TooltipContent>
              </Tooltip>
            </div>
          ) : null}
          <div className="flipbookToolbarActions">
            {isAnnotationToolbarOpen ? (
              <div aria-label="Teaching tools" className="flipbookAnnotationTools" role="toolbar">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <LmsButton
                      aria-label="Select and pan"
                      aria-pressed={annotationTool === 'none'}
                      className={`flipbookAnnotationTool${annotationTool === 'none' ? ' isActive' : ''}`}
                      disabled={isPageLoading}
                      onClick={() => { handleNonColorToolClick('none') }}
                      size="icon"
                      variant="ghost"
                    >
                      <FlipbookIcon className="size-4" name="mouse" />
                    </LmsButton>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Chọn / di chuyển</TooltipContent>
              </Tooltip>
              <div className="flipbookAnnotationColor" ref={openPaletteTool === 'pen' ? colorPaletteRef : undefined}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <LmsButton
                        aria-expanded={openPaletteTool === 'pen'}
                        aria-haspopup="listbox"
                        aria-label="Pen and color"
                        aria-pressed={annotationTool === 'pen'}
                        className={`flipbookAnnotationTool${annotationTool === 'pen' ? ' isActive' : ''}`}
                        disabled={isPageLoading}
                        onClick={() => { handleColorToolClick('pen') }}
                        size="icon"
                        variant="ghost"
                      >
                        <FlipbookIcon className="size-4" name="brush" />
                      </LmsButton>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Bút vẽ và chọn màu</TooltipContent>
                </Tooltip>
                {renderColorPalette('pen')}
              </div>
              <div className="flipbookAnnotationColor" ref={openPaletteTool === 'highlighter' ? colorPaletteRef : undefined}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <LmsButton
                        aria-expanded={openPaletteTool === 'highlighter'}
                        aria-haspopup="listbox"
                        aria-label="Highlighter and color"
                        aria-pressed={annotationTool === 'highlighter'}
                        className={`flipbookAnnotationTool${annotationTool === 'highlighter' ? ' isActive' : ''}`}
                        disabled={isPageLoading}
                        onClick={() => { handleColorToolClick('highlighter') }}
                        size="icon"
                        variant="ghost"
                      >
                        <FlipbookIcon className="size-4" name="brush-3" />
                      </LmsButton>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Highlight và chọn màu</TooltipContent>
                </Tooltip>
                {renderColorPalette('highlighter')}
              </div>
              <div className="flipbookAnnotationColor" ref={openPaletteTool === 'text' ? colorPaletteRef : undefined}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <LmsButton
                        aria-expanded={openPaletteTool === 'text'}
                        aria-haspopup="listbox"
                        aria-label="Text and color"
                        aria-pressed={annotationTool === 'text'}
                        className={`flipbookAnnotationTool${annotationTool === 'text' ? ' isActive' : ''}`}
                        disabled={isPageLoading}
                        onClick={() => { handleColorToolClick('text') }}
                        size="icon"
                        variant="ghost"
                      >
                        <FlipbookIcon className="size-4" name="text" />
                      </LmsButton>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Thêm chữ và chọn màu</TooltipContent>
                </Tooltip>
                {renderColorPalette('text')}
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <LmsButton
                      aria-label="Eraser"
                      aria-pressed={annotationTool === 'eraser'}
                      className={`flipbookAnnotationTool${annotationTool === 'eraser' ? ' isActive' : ''}`}
                      disabled={isPageLoading}
                      onClick={() => { handleNonColorToolClick('eraser') }}
                      size="icon"
                      variant="ghost"
                    >
                      <FlipbookIcon className="size-4" name="eraser" />
                    </LmsButton>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Tẩy ghi chú</TooltipContent>
              </Tooltip>
              {annotationHistory.selectedTextColor !== null ? (
                <div aria-label="Text alignment" className="flipbookAnnotationAlignment" role="toolbar">
                  {renderTextAlignment('left', 'Căn trái', <FlipbookIcon className="size-4" name="align-left" />)}
                  {renderTextAlignment('center', 'Căn giữa', <FlipbookIcon className="size-4" name="align-center" />)}
                  {renderTextAlignment('right', 'Căn phải', <FlipbookIcon className="size-4" name="align-right" />)}
                </div>
              ) : null}
              {annotationHistory.selectedTextColor !== null || annotationHistory.selectedStrokeColor !== null ? (
                <div aria-label="Arrange annotation layer" className="flipbookAnnotationLayerControls" role="toolbar">
                  {renderLayerAction('Đưa lên trên', <FlipbookIcon className="size-4" name="arrange-up" />, annotationHistory.bringSelectedForward)}
                  {renderLayerAction('Đưa lên đầu', <FlipbookIcon className="size-4" name="arrange-front" />, annotationHistory.bringSelectedToFront)}
                  {renderLayerAction('Đưa xuống dưới', <FlipbookIcon className="size-4" name="arrange-down" />, annotationHistory.sendSelectedBackward)}
                  {renderLayerAction('Đưa xuống dưới cùng', <FlipbookIcon className="size-4" name="arrange-back" />, annotationHistory.sendSelectedToBack)}
                </div>
              ) : null}
              <span aria-hidden="true" className="flipbookAnnotationDivider" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <LmsButton aria-label="Undo annotation" className="flipbookAnnotationTool" disabled={!annotationHistory.canUndo || isPageLoading} onClick={annotationHistory.undo} size="icon" variant="ghost">
                      <FlipbookIcon className="size-4" name="undo" />
                    </LmsButton>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Hoàn tác · Ctrl/Cmd+Z</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <LmsButton aria-label="Redo annotation" className="flipbookAnnotationTool" disabled={!annotationHistory.canRedo || isPageLoading} onClick={annotationHistory.redo} size="icon" variant="ghost">
                      <FlipbookIcon className="size-4" name="redo" />
                    </LmsButton>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Làm lại · Ctrl/Cmd+Shift+Z</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <LmsButton aria-label="Clear annotations" className="flipbookAnnotationTool" disabled={!annotationHistory.canClear || isPageLoading} onClick={annotationHistory.clear} size="icon" variant="ghost">
                      <FlipbookIcon className="size-4" name="trash" />
                    </LmsButton>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Xoá ghi chú</TooltipContent>
              </Tooltip>
              </div>
            ) : null}
          </div>
            <div className="flipbookToolbarViewActions">
              <div className="flipbookToolbarZoom">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <LmsButton aria-label="Zoom out" className="size-10 rounded-none border-0 text-[var(--lms-color-ink-strong)] shadow-none hover:bg-[var(--lms-color-surface-muted)]" disabled={zoom <= 1 || isPageLoading} onClick={onZoomOut} size="icon" variant="ghost">
                      <FlipbookIcon className="size-4" name="search-zoom-out" />
                    </LmsButton>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Thu nhỏ</TooltipContent>
              </Tooltip>
              <span className="flipbookToolbarZoomValue">
                {Math.round(zoom * 100)}%
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <LmsButton aria-label="Zoom in" className="size-10 rounded-none border-0 text-[var(--lms-color-ink-strong)] shadow-none hover:bg-[var(--lms-color-surface-muted)]" disabled={zoom >= FLIPBOOK_MAX_ZOOM || isPageLoading} onClick={onZoomIn} size="icon" variant="ghost">
                      <FlipbookIcon className="size-4" name="search-zoom-in" />
                    </LmsButton>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Phóng to</TooltipContent>
              </Tooltip>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <LmsButton aria-label={isFullscreen ? 'Exit fullscreen' : 'Open fullscreen'} className="size-10 bg-[var(--lms-color-surface-card)] text-[var(--lms-color-ink-strong)] shadow-sm hover:bg-[var(--lms-color-surface-muted)]" onClick={onToggleFullscreen} size="icon" variant="outline">
                      {isFullscreen ? <FlipbookIcon className="size-4" name="minimize" /> : <FlipbookIcon className="size-4" name="maximize" />}
                    </LmsButton>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{isFullscreen ? 'Thoát toàn màn hình' : 'Toàn màn hình'}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <LmsButton aria-label="Toggle teaching tools" aria-pressed={isAnnotationToolbarOpen} className={`flipbookToolbarNoteToggle${isAnnotationToolbarOpen ? ' isActive' : ''}`} onClick={() => { setIsAnnotationToolbarOpen((current) => !current) }} size="icon" variant="outline">
                      <FlipbookIcon className="size-4" name="note" />
                    </LmsButton>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{isAnnotationToolbarOpen ? 'Ẩn công cụ ghi chú' : 'Hiện công cụ ghi chú'}</TooltipContent>
              </Tooltip>
            </div>
        </div>
      </header>
    </TooltipProvider>
  )
}
